/**
 * Adoption-tracking documents (E2) — the `adoption` Cosmos container.
 *
 * WHAT THIS CONTAINER IS FOR: answering one question per week — is the admin
 * actually being used, and by whom. It holds counters and dedup markers, not
 * content.
 *
 * ---------------------------------------------------------------------------
 * The partition key is the design
 * ---------------------------------------------------------------------------
 *
 * Every document carries `toolWeek` = `${tool}:${isoWeek}` (e.g.
 * "brandbox-admin:2026-W33"), and that is the container's partition key. One
 * tool-week is therefore ONE logical partition, which buys two things at once:
 *
 *   - WRITES: a logical partition is exactly the scope in which Cosmos supports a
 *     transactional batch. So "this person is now marked seen" and "the counter
 *     went up" are written atomically and can never disagree.
 *   - READS: it is also exactly the scope every read needs. Twelve weeks of
 *     history is twelve bounded single-partition queries, flat in RU as history
 *     grows — instead of a cross-partition fan-out that gets more expensive every
 *     week, which is the classic RU cliff on a serverless account.
 *
 * The `daily` document derives its `toolWeek` from its own day's ISO week so it
 * shares the partition. That is what lets one query return volume AND people.
 *
 * ---------------------------------------------------------------------------
 * Retention: three tiers, enforced by per-item TTL
 * ---------------------------------------------------------------------------
 *
 *   35 days   `seen` / `action_seen` — dedup scratchpad. Only ever needs to
 *             outlive the week it dedups, and 35d ≫ 7d.
 *   400 days  `person` — carries a display name, so it is the one kind holding
 *             anything identifying. 400d is a comfortable superset of the 12-week
 *             window the card shows and the 52-week read clamp.
 *   forever   `weekly` / `action_weekly` / `daily` — integer rollups. These are
 *             the permanent history, which is why they are frozen at write time
 *             rather than recomputed from a scratchpad that no longer exists.
 *
 * The container sets `defaultTtl: -1`: TTL is ENABLED but expires nothing by
 * default, so only documents that carry their own `ttl` are ever reaped.
 *
 * ⚠️ THIS IS WHY EVERY SCHEMA BELOW IS `.strict()`. The permanent kinds do not
 * declare `ttl`, so a stray `ttl` on a `weekly`, `action_weekly` or `daily`
 * document fails validation instead of silently scheduling permanent history for
 * deletion. That is a guard, not pedantry — the failure it prevents is invisible
 * until the history is already gone.
 *
 * ---------------------------------------------------------------------------
 * Strict on write, projected on read
 * ---------------------------------------------------------------------------
 *
 * These schemas validate documents the store is ABOUT TO WRITE. They are not used
 * to parse point-reads, because Cosmos decorates stored documents with `_rid`,
 * `_self`, `_attachments` and friends, which `.strict()` would reject. The read
 * path never point-reads these documents anyway — it issues projection queries
 * (`SELECT c.docType, c.action, …`) that return only named fields.
 *
 * ---------------------------------------------------------------------------
 * What is and is not identifying
 * ---------------------------------------------------------------------------
 *
 * `personHash` is a salted one-way digest, never a UPN. `person.displayName` IS a
 * human name, on purpose — the adoption card answers "who", not just "how many".
 * So: the aggregate feed (`/api/metrics/weekly`) carries no identity because it
 * reads only the rollup kinds, and that is a structural property of which
 * documents it touches rather than a filter someone could delete. It is NOT a
 * claim that this container stores nobody.
 */

import { z } from 'zod';

import { EntityIdSchema, IsoTimestampSchema } from './common.js';

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** The tool a document belongs to. This app only ever writes 'brandbox-admin'. */
export const ADOPTION_TOOL = 'brandbox-admin';

/** ISO week, week-numbering year — "2026-W33". */
export const AdoptionIsoWeekSchema = z
  .string()
  .regex(/^\d{4}-W\d{2}$/, 'expected YYYY-Www');
export type AdoptionIsoWeek = z.infer<typeof AdoptionIsoWeekSchema>;

/** Brussels calendar day — "2026-08-17". */
export const AdoptionDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export type AdoptionDay = z.infer<typeof AdoptionDaySchema>;

/** Partition key value — "<tool>:<isoWeek>". */
export const ToolWeekSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]*:\d{4}-W\d{2}$/, 'expected <tool>:YYYY-Www');
export type ToolWeek = z.infer<typeof ToolWeekSchema>;

/**
 * Salted SHA-256 of the UPN, lowercase hex. 64 chars by construction — pinned
 * here so a raw address can never be stored in this field by accident.
 */
export const PersonHashSchema = z
  .string()
  .regex(/^[0-9a-f]{64}$/, 'expected a 64-char lowercase hex digest');
export type PersonHash = z.infer<typeof PersonHashSchema>;

/**
 * The four tracked actions — one reach signal plus three depth signals.
 *
 * `brandbox_opened` is deliberately NOT the bare key `opened`: the AI Portal
 * merges every solution's labels into ONE shared map, where Catalog Search
 * already owns `opened`. Emitting the bare key would render "Opened Catalog
 * Search" on the BrandBox card.
 *
 * Deliberately absent: opening a submission (a second reach signal dressed as
 * depth, and it rises with customer demand rather than staff adoption), the Audit
 * Log and Users pages (housekeeping), and the Live Website link (that is someone
 * leaving). The principle is: count what a person CHOSE to do, not what landed in
 * their inbox.
 */
export const AdoptionActionSchema = z.enum([
  'brandbox_opened',
  'box_created',
  'box_published',
  'submission_actioned',
]);
export type AdoptionAction = z.infer<typeof AdoptionActionSchema>;

/** The six document kinds sharing the container. */
export const AdoptionDocTypeSchema = z.enum([
  'seen',
  'action_seen',
  'weekly',
  'action_weekly',
  'daily',
  'person',
]);
export type AdoptionDocType = z.infer<typeof AdoptionDocTypeSchema>;

// ---------------------------------------------------------------------------
// Retention
// ---------------------------------------------------------------------------

const DAY_SECONDS = 24 * 60 * 60;

/** Dedup scratchpad retention. Must exceed one week; 35d gives ample slack. */
export const SEEN_TTL_SECONDS = 35 * DAY_SECONDS;

/**
 * `person` retention — the only kind holding a display name.
 *
 * Bounded on purpose. If `person` were permanent while `action_seen` (its exact
 * key, minus the name) expired at 35 days, the prune would delete nothing that was
 * not retained in richer form right beside it: the privacy claim would be false,
 * and the rollups would become derivable at read time, making the write-time
 * freeze a cache rather than a necessity. Bounding it is what makes both true.
 */
export const PERSON_TTL_SECONDS = 400 * DAY_SECONDS;

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** Fields every adoption document carries. */
const adoptionBase = {
  id: EntityIdSchema,
  toolWeek: ToolWeekSchema,
  tool: z.string().min(1),
};

/**
 * Dedup marker: this person acted at all during this week.
 *
 * Its existence is the answer to "have we already counted them?" — the 409 from a
 * failed Create IS the dedup result, so the write path never queries.
 *
 * `engaged` is upgraded false→true by a CONDITIONAL patch when the person's first
 * real action follows a bare page open, which is why it lives here rather than in a
 * seventh document kind.
 */
export const SeenDocSchema = z
  .object({
    ...adoptionBase,
    docType: z.literal('seen'),
    isoWeek: AdoptionIsoWeekSchema,
    personHash: PersonHashSchema,
    engaged: z.boolean(),
    createdAt: IsoTimestampSchema,
    ttl: z.number().int().positive(),
  })
  .strict();
export type SeenDoc = z.infer<typeof SeenDocSchema>;

/** Dedup marker: this person performed this specific action during this week. */
export const ActionSeenDocSchema = z
  .object({
    ...adoptionBase,
    docType: z.literal('action_seen'),
    isoWeek: AdoptionIsoWeekSchema,
    action: AdoptionActionSchema,
    personHash: PersonHashSchema,
    createdAt: IsoTimestampSchema,
    ttl: z.number().int().positive(),
  })
  .strict();
export type ActionSeenDoc = z.infer<typeof ActionSeenDocSchema>;

/**
 * Permanent per-week rollup. `uniqueUsers >= engagedUsers` always, guaranteed by
 * construction: the engaged counter can only move once the `seen` marker exists.
 *
 * No `ttl` — and `.strict()` makes adding one a validation error.
 */
export const WeeklyRollupDocSchema = z
  .object({
    ...adoptionBase,
    docType: z.literal('weekly'),
    isoWeek: AdoptionIsoWeekSchema,
    uniqueUsers: z.number().int().nonnegative(),
    engagedUsers: z.number().int().nonnegative(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();
export type WeeklyRollupDoc = z.infer<typeof WeeklyRollupDocSchema>;

/** Permanent per-week, per-action rollup: how many DISTINCT people did it. */
export const ActionWeeklyRollupDocSchema = z
  .object({
    ...adoptionBase,
    docType: z.literal('action_weekly'),
    isoWeek: AdoptionIsoWeekSchema,
    action: AdoptionActionSchema,
    uniqueUsers: z.number().int().nonnegative(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();
export type ActionWeeklyRollupDoc = z.infer<typeof ActionWeeklyRollupDocSchema>;

/**
 * Permanent per-day, per-action VOLUME (every occurrence, not distinct people).
 *
 * Volume is explicitly non-authoritative: it is replayable and one cohort member
 * can inflate it. It rides in a different batch from the weekly counters, so a
 * crash between them can leave a counted unique user with no volume — an
 * under-count, never an over-count.
 */
export const DailyVolumeDocSchema = z
  .object({
    ...adoptionBase,
    docType: z.literal('daily'),
    day: AdoptionDaySchema,
    action: AdoptionActionSchema,
    totalCount: z.number().int().nonnegative(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();
export type DailyVolumeDoc = z.infer<typeof DailyVolumeDocSchema>;

/**
 * Per-week, per-action, per-person row — the ONLY kind carrying a display name.
 *
 * `displayName: null` means "counted, not named", which is a legitimate outcome
 * reported as a number (`unattributed`). It is never the UPN: an address is an
 * internal key, never a label. A later nameless event must not erase a name, which
 * is why the store emits the name-setting patch operation only when the name is
 * non-null.
 */
export const PersonUsageDocSchema = z
  .object({
    ...adoptionBase,
    docType: z.literal('person'),
    isoWeek: AdoptionIsoWeekSchema,
    action: AdoptionActionSchema,
    personHash: PersonHashSchema,
    displayName: z.string().min(1).max(256).nullable(),
    total: z.number().int().nonnegative(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    ttl: z.number().int().positive(),
  })
  .strict();
export type PersonUsageDoc = z.infer<typeof PersonUsageDocSchema>;

/** Any adoption document. */
export const AdoptionDocSchema = z.discriminatedUnion('docType', [
  SeenDocSchema,
  ActionSeenDocSchema,
  WeeklyRollupDocSchema,
  ActionWeeklyRollupDocSchema,
  DailyVolumeDocSchema,
  PersonUsageDocSchema,
]);
export type AdoptionDoc = z.infer<typeof AdoptionDocSchema>;

/** The kinds that must never carry a `ttl`. Used by the schema guard test. */
export const PERMANENT_ADOPTION_DOC_TYPES: ReadonlyArray<AdoptionDocType> = [
  'weekly',
  'action_weekly',
  'daily',
];
